import { useState, useEffect } from 'react';
import { Calculator, HelpCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import ToolPage from '@/components/ToolPage';
import { toolCardClass, toolInputClass, toolLabelClass, toolButtonOutlineClass } from '@/lib/toolStyles';

interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  remainingBalance: number;
}

interface LoanType {
  id: string;
  name: string;
  minAmount: number;
  maxAmount: number;
  minRate: number;
  maxRate: number;
  maxTermYears: number;
  description: string;
}

const loanTypes: LoanType[] = [
  {
    id: 'personal',
    name: 'Personal Loan',
    minAmount: 1000,
    maxAmount: 25000,
    minRate: 5.9,
    maxRate: 24.9,
    maxTermYears: 7,
    description: 'Unsecured loans for personal use with higher interest rates'
  },
  {
    id: 'car',
    name: 'Car Loan',
    minAmount: 3000,
    maxAmount: 50000,
    minRate: 3.5,
    maxRate: 7.9,
    maxTermYears: 5,
    description: 'Secured loans for vehicle purchases with lower rates'
  },
  {
    id: 'business',
    name: 'Business Loan',
    minAmount: 5000,
    maxAmount: 100000,
    minRate: 4.9,
    maxRate: 15.9,
    maxTermYears: 7,
    description: 'Loans for business purposes with varying rates based on business profile'
  },
];

export default function LoanCalculator() {
  const [loanAmount, setLoanAmount] = useState<string>('10000');
  const [interestRate, setInterestRate] = useState<string>('5.9');
  const [loanTerm, setLoanTerm] = useState(3);
  const [loanType, setLoanType] = useState<string>('personal');
  const [showAmortization, setShowAmortization] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState<string>('');

  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState<AmortizationRow[]>([]);

  const selectedLoanType = loanTypes.find(type => type.id === loanType)!;

  // Generate available loan terms based on loan type
  const getLoanTerms = () => {
    const terms = [];
    for (let i = 1; i <= selectedLoanType.maxTermYears; i++) {
      terms.push({ years: i, label: i === 1 ? '1 Year' : `${i} Years` });
    }
    return terms;
  };

  // Update interest rate when loan type changes
  useEffect(() => {
    setInterestRate(selectedLoanType.minRate.toString());
  }, [loanType]);

  // Validate input values
  useEffect(() => {
    const amount = Number(loanAmount);
    const rate = Number(interestRate);
    
    if (amount < selectedLoanType.minAmount) {
      setError(`Minimum loan amount for ${selectedLoanType.name} is ${formatCurrency(selectedLoanType.minAmount)}`);
      return;
    }
    
    if (amount > selectedLoanType.maxAmount) {
      setError(`Maximum loan amount for ${selectedLoanType.name} is ${formatCurrency(selectedLoanType.maxAmount)}`);
      return;
    }
    
    if (rate < selectedLoanType.minRate) {
      setError(`Minimum interest rate for ${selectedLoanType.name} is ${selectedLoanType.minRate}%`);
      return;
    }
    
    if (rate > selectedLoanType.maxRate) {
      setError(`Maximum interest rate for ${selectedLoanType.name} is ${selectedLoanType.maxRate}%`);
      return;
    }

    setError('');
  }, [loanAmount, interestRate, loanType]);

  useEffect(() => {
    if (!error) {
      calculateLoan();
    } else {
      setMonthlyPayment(0);
      setTotalInterest(0);
      setTotalPayment(0);
      setAmortizationSchedule([]);
    }
  }, [loanAmount, interestRate, loanTerm, error]);

  const calculateLoan = () => {
    const principal = Number(loanAmount) || 0;
    const annualRate = Number(interestRate) || 0;
    const monthlyRate = annualRate / 100 / 12;
    const totalMonths = loanTerm * 12;

    if (principal <= 0 || annualRate <= 0) {
      setMonthlyPayment(0);
      setTotalInterest(0);
      setTotalPayment(0);
      setAmortizationSchedule([]);
      return;
    }

    // Calculate monthly payment using the loan payment formula
    const monthlyPmt = principal * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / 
                      (Math.pow(1 + monthlyRate, totalMonths) - 1);

    setMonthlyPayment(monthlyPmt);
    
    // Calculate total payment and interest
    const totalPmt = monthlyPmt * totalMonths;
    setTotalPayment(totalPmt);
    setTotalInterest(totalPmt - principal);

    // Generate amortization schedule
    let balance = principal;
    const schedule: AmortizationRow[] = [];

    for (let month = 1; month <= totalMonths; month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPmt - interestPayment;
      balance -= principalPayment;

      schedule.push({
        month,
        payment: monthlyPmt,
        principal: principalPayment,
        interest: interestPayment,
        remainingBalance: Math.max(0, balance)
      });
    }

    setAmortizationSchedule(schedule);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const downloadAmortizationSchedule = () => {
    let csv = 'Month,Payment,Principal,Interest,Remaining Balance\n';
    
    amortizationSchedule.forEach(row => {
      csv += `${row.month},${row.payment.toFixed(2)},${row.principal.toFixed(2)},${row.interest.toFixed(2)},${row.remainingBalance.toFixed(2)}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'loan-amortization-schedule.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <ToolPage
      title="Loan Calculator"
      description="Calculate monthly payments, total interest, and amortization schedule"
      icon={Calculator}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className={toolCardClass}>
              <CardHeader>
                <h2 className="text-lg font-semibold text-slate-900">Loan Details</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={toolLabelClass}>Loan Type</Label>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    {loanTypes.map((type) => (
                      <Button key={type.id} variant={loanType === type.id ? 'gradient' : 'outline'} size="sm" className={`h-auto py-3 ${loanType !== type.id ? toolButtonOutlineClass : ''}`} onClick={() => setLoanType(type.id)}>
                        {type.name}
                      </Button>
                    ))}
                  </div>
                  <p className={`mt-2 text-sm ${toolLabelClass}`}>{selectedLoanType.description}</p>
                  <p className={`text-xs ${toolLabelClass}`}>Rate range: {selectedLoanType.minRate}% - {selectedLoanType.maxRate}% | Amount: {formatCurrency(selectedLoanType.minAmount)} - {formatCurrency(selectedLoanType.maxAmount)}</p>
                </div>
                <div className="space-y-2">
                  <Label className={toolLabelClass}>Loan Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input type="number" min={selectedLoanType.minAmount} max={selectedLoanType.maxAmount} value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)} className={`pl-8 ${toolInputClass}`} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={toolLabelClass}>Annual Interest Rate</Label>
                  <div className="relative">
                    <Input type="number" min={selectedLoanType.minRate} max={selectedLoanType.maxRate} step={0.1} value={interestRate} onChange={(e) => setInterestRate(e.target.value)} className={`pr-8 ${toolInputClass}`} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                  </div>
                </div>
                <div>
                  <Label className={toolLabelClass}>Loan Term</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {getLoanTerms().map((term) => (
                      <Button key={term.years} variant={loanTerm === term.years ? 'gradient' : 'outline'} size="sm" className={`h-auto py-3 ${loanTerm !== term.years ? toolButtonOutlineClass : ''}`} onClick={() => setLoanTerm(term.years)}>
                        {term.label}
                      </Button>
                    ))}
                  </div>
                </div>
                {error && (
                  <Alert variant="destructive" className="border-red-500/30 bg-red-500/10">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card className={toolCardClass}>
              <CardHeader className="flex flex-row justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-900">Results</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowInfo(!showInfo)} className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"><HelpCircle className="w-5 h-5" /></Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className={`text-sm ${toolLabelClass} mb-1`}>Monthly Payment</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(monthlyPayment)}</p>
                  </div>
                  <div>
                    <p className={`text-sm ${toolLabelClass} mb-1`}>Total Interest</p>
                    <p className="text-2xl font-bold text-brand-700">{formatCurrency(totalInterest)}</p>
                  </div>
                  <div>
                    <p className={`text-sm ${toolLabelClass} mb-1`}>Total Payment</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalPayment)}</p>
                  </div>
                </div>
                {showInfo && (
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className={`text-sm ${toolLabelClass}`}>The monthly payment includes both principal and interest. Total interest is what you pay beyond the loan amount. Total payment is loan amount plus interest.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className={toolCardClass}>
            <CardHeader className="flex flex-row justify-between items-center">
              <h2 className="text-lg font-semibold text-slate-900">Amortization Schedule</h2>
              <Button variant="outline" size="sm" className={toolButtonOutlineClass} onClick={downloadAmortizationSchedule} disabled={amortizationSchedule.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200 hover:bg-slate-50">
                    <TableHead className="text-slate-600">Month</TableHead>
                    <TableHead className="text-slate-600">Payment</TableHead>
                    <TableHead className="text-slate-600">Principal</TableHead>
                    <TableHead className="text-slate-600">Interest</TableHead>
                    <TableHead className="text-slate-600">Remaining</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {amortizationSchedule.slice(0, showAmortization ? undefined : 12).map((row) => (
                    <TableRow key={row.month} className="border-slate-200 hover:bg-slate-50">
                      <TableCell className="text-slate-900">{row.month}</TableCell>
                      <TableCell className="text-slate-900">{formatCurrency(row.payment)}</TableCell>
                      <TableCell className="text-slate-900">{formatCurrency(row.principal)}</TableCell>
                      <TableCell className="text-slate-900">{formatCurrency(row.interest)}</TableCell>
                      <TableCell className="text-slate-900">{formatCurrency(row.remainingBalance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {amortizationSchedule.length > 12 && (
                <div className="mt-4 text-center">
                  <Button variant="link" size="sm" className="text-brand-700 hover:text-brand-900" onClick={() => setShowAmortization(!showAmortization)}>
                    {showAmortization ? 'Show Less' : 'Show Full Schedule'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
    </ToolPage>
  );
}